import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';

import {LINKS} from '@/constants/LinksConstants';
import {ISplitParticipant} from '@/dataSource/src/api/splits/entities';
import {useSplitsRepository} from '@/dataSource/src/api/splits/useSplitsRepository';
import {useUsersRepository} from '@/dataSource/src/api/users/useRepository';
import {WEB_VIEW_MESSAGES} from '@/services/webview/constants';
import {formatPhoneForDisplay} from '@/utils/formatPhoneForDisplay';
import {getAvatarImage} from '@/utils/getAvatarImage';
import {getInitials} from '@/utils/getInitials';
import {isPhoneValid} from '@/utils/isPhoneValid';

import {
  PAYMENT_LINK_V2_FEATURES,
  PAYMENT_LINK_V2_QUERY_PARAMS,
} from '../paymentLink/v2/pages/paymentLinkV2/models/constants/constants';

import {
  OTP_LENGTH,
  RESEND_COUNTDOWN_SECONDS,
  SPLIT_REFERRAL_CODE,
  SPLITS_PAGES,
  SPLITS_QUERY_PARAMS,
  SPLITS_ROUTES,
  SPLITS_UTM,
  SPLITS_VIEW_STEP,
  SplitsViewStep,
} from './constants';
import {IFamilyBillingProfile, SplitsViewModelContextType} from './interfaces';
import {useSplitsTrackers} from './trackers';

const SplitsViewModelContext = createContext<
  SplitsViewModelContextType | undefined
>(undefined);

export function SplitsViewModelProvider({children}: {children: ReactNode}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const {profile} = useUsersRepository();

  const {
    handleGetSplitData,
    handleSaveParticipantPhone,
    handleValidateOtp,
    handleConfirmPayment,
    handleResendOtp,
    handleJoinLoggedUserToSplit,
    isLoadingSplitData,
    splitData,
    isError,
    error,
  } = useSplitsRepository(profile?.id);

  const ownerId = splitData?.owner_id;
  const owner = splitData?.participants.find(participant => participant.owner);
  const description = splitData?.detail.description;
  const totalAmountPaid = splitData?.detail.total;
  const participants = splitData?.participants;
  const paymentLink = splitData?.payment_link;
  const metadataReferralCode = splitData?.metadata?.referral_code;
  const inviterName = owner?.name || 'Someone';

  const {
    trackViewSplitsLanding,
    trackViewSplitsSelectMember,
    trackSelectSplitsMember,
    trackSelectSplitsContinue,
    trackViewSplitsPhoneInput,
    trackSelectSplitsBackPhoneInput,
    trackSelectSplitsPhoneContinue,
    trackViewSplitsOtp,
    trackSelectSplitsResendOtp,
    trackSelectSplitsSubmitOtp,
    trackSelectSplitsOpenAccount,
    trackApiSplitsGetDataSuccess,
    trackApiSplitsGetDataError,
    trackSelectSplitsGoToPay,
    trackViewSplitsConfirmation,
    trackViewSplitsError,
    trackViewSplitsSuccess,
  } = useSplitsTrackers(ownerId, profile?.id);

  const [step, setStep] = useState<SplitsViewStep>(SPLITS_VIEW_STEP.LANDING);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [resendSeconds, setResendSeconds] = useState(0);
  const [participantId, setParticipantId] = useState<string | null>(null);
  const [selectedParticipant, setSelectedParticipant] =
    useState<ISplitParticipant | null>();
  const location = useLocation();
  const splitId = searchParams.get(SPLITS_QUERY_PARAMS.SPLIT_ID);
  const isOnErrorPage = location.pathname === SPLITS_ROUTES.ERROR;

  const payerMetadata: IFamilyBillingProfile = useMemo(() => {
    if (profile) {
      return {
        firstName: profile?.firstName,
        lastName: profile?.lastName,
        email: profile?.email,
        phone: profile?.phone,
        additionalAddress: profile?.billingAddress?.additional_address,
        city: profile?.billingAddress?.city,
        state: profile?.billingAddress?.state,
        zipCode: profile?.billingAddress?.zip_code,
        country: profile?.billingAddress?.country,
        line: profile?.billingAddress?.line,
        profilePicture: profile?.metadata?.PROFILE_PICTURE,
      };
    }
    return JSON.parse(
      searchParams.get(SPLITS_QUERY_PARAMS.PAYER_METADATA) ?? '{}',
    );
  }, [profile, searchParams]);

  const hasPayerMetadata = useMemo(
    () =>
      payerMetadata &&
      typeof payerMetadata === 'object' &&
      Object.keys(payerMetadata).length > 0,
    [payerMetadata],
  );

  const handleSplitDataError = useCallback(
    err => {
      navigate(`${SPLITS_ROUTES.ERROR}?${searchParams.toString()}`, {
        state: {errorStatus: err?.status},
      });
    },
    [navigate, searchParams],
  );

  const fetchSplitData = useCallback(async () => {
    if (isOnErrorPage) return;
    if (!splitId) {
      handleSplitDataError({status: 404});
      return;
    }
    try {
      await handleGetSplitData(splitId);
    } catch (err) {
      handleSplitDataError(err);
    }
  }, [splitId, isOnErrorPage, handleGetSplitData, handleSplitDataError]);

  useEffect(() => {
    fetchSplitData();
  }, [fetchSplitData]);

  useEffect(() => {
    if (splitId && splitData) {
      trackApiSplitsGetDataSuccess(splitId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [splitId, splitData]);

  useEffect(() => {
    if (splitId && isError) {
      trackApiSplitsGetDataError(
        splitId,
        error != null ? String(error) : undefined,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [splitId, isError, error]);

  useEffect(() => {
    if (!splitId || isError || currentPage !== SPLITS_PAGES.LANDING) return;
    switch (step) {
      case SPLITS_VIEW_STEP.LANDING:
        trackViewSplitsLanding(splitId, inviterName);
        break;
      case SPLITS_VIEW_STEP.SELECT_MEMBER:
        trackViewSplitsSelectMember(splitId, inviterName);
        break;
      case SPLITS_VIEW_STEP.PHONE_INPUT:
        trackViewSplitsPhoneInput(splitId);
        break;
      case SPLITS_VIEW_STEP.OTP:
        trackViewSplitsOtp(splitId);
        break;
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, splitId]);

  const currentUserIsAParticipant = useMemo(() => {
    if (!profile?.id || !participants) return false;
    return participants.some(p => p.modak_user_id === profile.id);
  }, [participants, profile?.id]);

  useEffect(() => {
    const isInSuccess = location.pathname === SPLITS_ROUTES.SUCCESS;
    const isInError = location.pathname === SPLITS_ROUTES.ERROR;
    if (currentUserIsAParticipant && !isInSuccess && !isInError) {
      navigate(`${SPLITS_ROUTES.CONFIRMATION}?${searchParams.toString()}`, {
        state: {otpVerified: true},
      });
    }
  }, [
    currentUserIsAParticipant,
    location.pathname,
    navigate,
    payerMetadata,
    phone,
    searchParams,
    splitId,
  ]);

  const inviterInitials = useMemo(
    () => getInitials(inviterName),
    [inviterName],
  );

  const formattedPhone = useMemo(() => formatPhoneForDisplay(phone), [phone]);
  const isPhoneValidFlag = useMemo(() => isPhoneValid(phone), [phone]);
  const canResend = resendSeconds === 0;

  const selectableParticipants = useMemo(() => {
    const baseParticipants = (participants ?? []).filter(
      p => !p.owner && !p.payment_confirmed,
    );

    const loggedUserId = profile?.id;
    if (!loggedUserId) return baseParticipants;

    const hasParticipantWithUser = baseParticipants.some(
      p => p.modak_user_id === loggedUserId,
    );

    return baseParticipants.filter(p =>
      hasParticipantWithUser
        ? p.modak_user_id === loggedUserId
        : p.modak_user_id !== loggedUserId,
    );
  }, [participants, profile]);

  const onContinueFromLanding = useCallback(() => {
    trackSelectSplitsContinue(splitId);
    setStep(SPLITS_VIEW_STEP.SELECT_MEMBER);
  }, [splitId, trackSelectSplitsContinue]);

  const onSelectParticipant = useCallback((participant: ISplitParticipant) => {
    setSelectedParticipant(participant);
  }, []);

  const handleSelectParticipantAndGoToConfirmationScreen = useCallback(
    (selectedParticipantId: string) => {
      setParticipantId(selectedParticipantId);
      searchParams.set(
        SPLITS_QUERY_PARAMS.PARTICIPANT_ID,
        selectedParticipantId,
      );
      navigate(`${SPLITS_ROUTES.CONFIRMATION}?${searchParams.toString()}`, {
        state: {otpVerified: true},
      });
    },
    [navigate, searchParams],
  );

  const onContinueFromSelectParticipant = useCallback(async () => {
    if (!selectedParticipant) return;
    trackSelectSplitsMember(splitId, selectedParticipant.split_user_id);

    if (selectedParticipant.reminder_enabled) {
      handleSelectParticipantAndGoToConfirmationScreen(
        selectedParticipant.split_user_id,
      );
      return;
    }

    if (profile && !selectedParticipant.modak_user_id) {
      const success = await handleJoinLoggedUserToSplit({
        split_id: splitId,
        participant_id: selectedParticipant.split_user_id,
      });
      if (success) {
        handleSelectParticipantAndGoToConfirmationScreen(
          selectedParticipant.split_user_id,
        );
      }
      return;
    }

    if (profile && selectedParticipant.modak_user_id === profile.id) {
      handleSelectParticipantAndGoToConfirmationScreen(
        selectedParticipant.split_user_id,
      );
      return;
    }

    setStep(SPLITS_VIEW_STEP.PHONE_INPUT);
  }, [
    selectedParticipant,
    trackSelectSplitsMember,
    splitId,
    profile,
    handleSelectParticipantAndGoToConfirmationScreen,
    handleJoinLoggedUserToSplit,
  ]);

  const onSubmitPhone = useCallback(async () => {
    if (!isPhoneValid(phone) || !selectedParticipant) return;
    trackSelectSplitsPhoneContinue(splitId);
    const response = await handleSaveParticipantPhone({
      split_id: splitId,
      phone_number: phone,
      participant_id: selectedParticipant.split_user_id,
    });
    setParticipantId(response.participant_id);
    setStep(SPLITS_VIEW_STEP.OTP);
    setOtp('');
    setResendSeconds(RESEND_COUNTDOWN_SECONDS);
  }, [
    phone,
    selectedParticipant,
    splitId,
    handleSaveParticipantPhone,
    trackSelectSplitsPhoneContinue,
  ]);

  const splitParticipantId: string | undefined = useMemo(() => {
    return (
      participantId ||
      searchParams.get(SPLITS_QUERY_PARAMS.PARTICIPANT_ID) ||
      (profile?.id &&
        participants?.length > 0 &&
        participants.find(p => p.modak_user_id === profile?.id)?.split_user_id)
    );
  }, [participantId, searchParams, profile?.id, participants]);

  const currentSplitParticipant = useMemo(() => {
    if (!splitParticipantId || !participants) return null;
    return (
      participants.find(p => p.split_user_id === splitParticipantId) ?? null
    );
  }, [splitParticipantId, participants]);

  const userName: string = useMemo(() => {
    return profile?.firstName || currentSplitParticipant?.name || inviterName;
  }, [profile?.firstName, currentSplitParticipant?.name, inviterName]);

  const onGetAvatarImage = useCallback(
    (inviter: string) =>
      getAvatarImage(
        payerMetadata?.profilePicture,
        getInitials(inviter || inviterName),
      ),
    [payerMetadata?.profilePicture, inviterName],
  );

  const onResendOtp = useCallback(async () => {
    if (!canResend || !isPhoneValid(phone)) return;
    if (!splitId || !splitParticipantId) return;
    trackSelectSplitsResendOtp(splitId);
    await handleResendOtp({
      split_id: splitId,
      participant_id: splitParticipantId,
    });
    setResendSeconds(RESEND_COUNTDOWN_SECONDS);
  }, [
    canResend,
    handleResendOtp,
    phone,
    splitId,
    splitParticipantId,
    trackSelectSplitsResendOtp,
  ]);

  const onSubmitOtp = useCallback(
    async (otpValue?: string) => {
      const code = otpValue ?? otp;
      if (code.length !== OTP_LENGTH) return;
      if (!splitId || !participantId) return;
      trackSelectSplitsSubmitOtp(splitId);
      try {
        await handleValidateOtp({
          split_id: splitId,
          participant_id: participantId,
          otp: code,
        });
        searchParams.set(SPLITS_QUERY_PARAMS.PARTICIPANT_ID, participantId);
        navigate(`${SPLITS_ROUTES.CONFIRMATION}?${searchParams.toString()}`, {
          state: {otpVerified: true},
        });
      } catch {
        return;
      }
    },
    [
      handleValidateOtp,
      navigate,
      otp,
      participantId,
      searchParams,
      splitId,
      trackSelectSplitsSubmitOtp,
    ],
  );

  useEffect(() => {
    if (step !== SPLITS_VIEW_STEP.OTP || resendSeconds <= 0) return;
    const id = setInterval(() => {
      setResendSeconds(s => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [step, resendSeconds]);

  const allParticipantsPaid: boolean = useMemo(() => {
    return (
      participants?.length > 0 && participants.every(p => p.payment_confirmed)
    );
  }, [participants]);

  const isCurrentParticipantPaid = useMemo(() => {
    if (!splitParticipantId || !participants) return false;
    const participant = participants.find(
      p => p.split_user_id === splitParticipantId,
    );
    return participant?.payment_confirmed ?? false;
  }, [splitParticipantId, participants]);

  const currentPage = useMemo(() => {
    switch (location.pathname) {
      case SPLITS_ROUTES.CONFIRMATION:
        trackViewSplitsConfirmation(splitId, splitParticipantId);
        return SPLITS_PAGES.CONFIRMATION;
      case SPLITS_ROUTES.SUCCESS:
        trackViewSplitsSuccess(splitId, splitParticipantId);
        return SPLITS_PAGES.SUCCESS;
      case SPLITS_ROUTES.ERROR:
        trackViewSplitsError(splitId, splitParticipantId);
        return SPLITS_PAGES.ERROR;
      default:
        return SPLITS_PAGES.LANDING;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const currentUtm = useMemo(() => {
    switch (currentPage) {
      case SPLITS_PAGES.CONFIRMATION:
        return SPLITS_UTM.BREAKDOWN;
      case SPLITS_PAGES.SUCCESS:
        return SPLITS_UTM.COMPLETED;
      default:
        return SPLITS_UTM.INTRO;
    }
  }, [currentPage]);

  useEffect(() => {
    if (
      currentPage === SPLITS_PAGES.SUCCESS ||
      currentPage === SPLITS_PAGES.ERROR
    )
      return;
    if (allParticipantsPaid || isCurrentParticipantPaid) {
      searchParams.set(SPLITS_QUERY_PARAMS.ALREADY_PAID, '1');
      navigate(`${SPLITS_ROUTES.SUCCESS}?${searchParams.toString()}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allParticipantsPaid,
    isCurrentParticipantPaid,
    searchParams,
    currentPage,
  ]);

  const alreadyPaid = useMemo(
    () => searchParams.get(SPLITS_QUERY_PARAMS.ALREADY_PAID) === '1',
    [searchParams],
  );

  const referralCode = useMemo(() => {
    if (metadataReferralCode) {
      return metadataReferralCode;
    }
    const codeFromUrl = searchParams.get(SPLITS_QUERY_PARAMS.REFERRAL_CODE);
    if (codeFromUrl) return codeFromUrl;
    return SPLIT_REFERRAL_CODE;
  }, [searchParams, metadataReferralCode]);

  const onOpenAccount = useCallback(() => {
    trackSelectSplitsOpenAccount(splitId);

    const url = LINKS.MODAK_GET_STARTED(
      currentUtm.SOURCE,
      currentUtm.MEDIUM,
      currentUtm.CAMPAIGN,
      currentUtm.CONTENT,
      referralCode,
    );
    const newWindow = window.open(url, '_blank');
    if (newWindow) {
      newWindow.focus();
      newWindow.opener = null;
    }
  }, [
    trackSelectSplitsOpenAccount,
    splitId,
    currentUtm.SOURCE,
    currentUtm.MEDIUM,
    currentUtm.CAMPAIGN,
    currentUtm.CONTENT,
    referralCode,
  ]);

  const handlePay = useCallback(() => {
    let baseAmount = 0;
    if (participants && participants.length > 0) {
      const participant = participants.find(
        p => p.split_user_id === splitParticipantId,
      );
      if (participant) {
        baseAmount = participant.split_amount;
      } else {
        const firstAmount = participants[0].split_amount;
        const allEqual = participants.every(
          p => p.split_amount === firstAmount,
        );
        baseAmount = allEqual ? Number(firstAmount) : 0;
      }
    }
    searchParams.set(
      PAYMENT_LINK_V2_QUERY_PARAMS.BASE_AMOUNT,
      baseAmount.toString(),
    );

    searchParams.set(
      PAYMENT_LINK_V2_QUERY_PARAMS.FEATURE,
      PAYMENT_LINK_V2_FEATURES.SPLITS,
    );

    const paymentLinkUrl = `${paymentLink}?${searchParams.toString()}`;
    trackSelectSplitsGoToPay(splitId, splitParticipantId, paymentLinkUrl);

    const urlForNavigate = paymentLinkUrl.split('/').pop();

    navigate(`/payment-link/v2/${urlForNavigate}`);
  }, [
    navigate,
    participants,
    paymentLink,
    searchParams,
    splitId,
    splitParticipantId,
    trackSelectSplitsGoToPay,
  ]);

  const inApp = useMemo(
    () =>
      searchParams.get(SPLITS_QUERY_PARAMS.IN_APP) === '1' || hasPayerMetadata,
    [searchParams, hasPayerMetadata],
  );

  const handleOpenAccount = useCallback(() => {
    trackSelectSplitsOpenAccount(splitId);
    const url = LINKS.MODAK_GET_STARTED(
      currentUtm.SOURCE,
      currentUtm.MEDIUM,
      currentUtm.CAMPAIGN,
      currentUtm.CONTENT,
      referralCode,
    );
    window.open(url, '_blank');
  }, [
    trackSelectSplitsOpenAccount,
    splitId,
    currentUtm.SOURCE,
    currentUtm.MEDIUM,
    currentUtm.CAMPAIGN,
    currentUtm.CONTENT,
    referralCode,
  ]);

  const handleSplitWithMo = useCallback(() => {
    const splitBillsMobileRoute = 'MO_ASSISTANT_AI_PAGE/true';
    if (
      typeof window.ReactNativeWebView !== 'undefined' &&
      window.ReactNativeWebView
    ) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          status: WEB_VIEW_MESSAGES.OPEN_SPLITS,
          link: splitBillsMobileRoute,
        }),
      );
      return;
    }

    navigate(`/split-bills`);
  }, [navigate]);

  const onHandleConfirmPayment = useCallback(async (): Promise<void> => {
    if (!splitId || !splitParticipantId) return;
    await handleConfirmPayment({
      participant_id: splitParticipantId,
      split_id: splitId,
    });
  }, [handleConfirmPayment, splitId, splitParticipantId]);

  const canGoBack = useMemo(() => {
    return (
      step === SPLITS_VIEW_STEP.OTP ||
      step === SPLITS_VIEW_STEP.PHONE_INPUT ||
      step === SPLITS_VIEW_STEP.SELECT_MEMBER
    );
  }, [step]);

  const handleBack = useCallback(() => {
    if (step === SPLITS_VIEW_STEP.OTP) {
      trackSelectSplitsBackPhoneInput(splitId);
      setStep(SPLITS_VIEW_STEP.PHONE_INPUT);
    } else if (step === SPLITS_VIEW_STEP.PHONE_INPUT) {
      setStep(SPLITS_VIEW_STEP.SELECT_MEMBER);
    } else if (step === SPLITS_VIEW_STEP.SELECT_MEMBER) {
      setStep(SPLITS_VIEW_STEP.LANDING);
    }
  }, [step, splitId, trackSelectSplitsBackPhoneInput]);

  const value = useMemo<SplitsViewModelContextType>(
    () => ({
      step,
      userName,
      inviterName,
      inviterInitials,
      totalAmountPaid,
      description,
      phone,
      participants,
      paymentLink,
      setPhone,
      formattedPhone,
      isPhoneValid: isPhoneValidFlag,
      otp,
      setOtp,
      resendSeconds,
      canResend,
      onContinueFromLanding,
      onSubmitPhone,
      onResendOtp,
      onSubmitOtp,
      onOpenAccount,
      handlePay,
      owner,
      onGetAvatarImage,
      isLoadingSplitData,
      inApp,
      handleOpenAccount,
      handleSplitWithMo,
      participantId,
      onHandleConfirmPayment,
      splitParticipantId,
      alreadyPaid,
      canGoBack,
      handleBack,
      selectableParticipants,
      selectedParticipant,
      onSelectParticipant,
      onContinueFromSelectParticipant,
    }),
    [
      step,
      userName,
      inviterName,
      inviterInitials,
      totalAmountPaid,
      description,
      phone,
      participants,
      paymentLink,
      formattedPhone,
      isPhoneValidFlag,
      otp,
      resendSeconds,
      canResend,
      onContinueFromLanding,
      onSubmitPhone,
      onResendOtp,
      onSubmitOtp,
      onOpenAccount,
      handlePay,
      owner,
      onGetAvatarImage,
      isLoadingSplitData,
      inApp,
      handleOpenAccount,
      handleSplitWithMo,
      participantId,
      onHandleConfirmPayment,
      splitParticipantId,
      alreadyPaid,
      canGoBack,
      handleBack,
      selectableParticipants,
      selectedParticipant,
      onSelectParticipant,
      onContinueFromSelectParticipant,
    ],
  );

  return (
    <SplitsViewModelContext.Provider value={value}>
      {children}
    </SplitsViewModelContext.Provider>
  );
}

export const useSplitBillsViewModelProvider = () => {
  const context = useContext(SplitsViewModelContext);
  if (!context) {
    throw new Error(
      'useSplitsViewModelProvider must be used within SplitsViewModelProvider',
    );
  }
  return context;
};
